import { CheckboxGroupButtons } from '@/components/_common';
import {
  Checkbox,
  Col,
  Form,
  FormInstance,
  Radio,
  Row,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import FinancialCashFlowDateForm from './FinancialCashFlowDateForm';
import FinancialCashFlowMoreOptions from './FinancialCashFlowMoreOptions';
import {
  FilterByDate,
  FinancialCashFlowFormValues,
  Situation,
  TypeReport,
} from '@/core/domain/NetReport/Financial/Financial.types';

interface Props {
  form: FormInstance<FinancialCashFlowFormValues>;
}

export default function FinancialCashFlowForm({ form }: Props) {
  const intl = useIntl().formatMessage;

  const option = (option: string) => intl({ id: `cashFlow.option.${option}` });
  const { Title } = Typography;

  const situationOption = [
    { value: Situation.open, label: option('open') },
    { value: Situation.liquidated, label: option('liquidated') },
  ];

  const filterByDateOption = [
    { value: FilterByDate.dueDate, label: option('expiration') },
    { value: FilterByDate.payDate, label: option('payment') },
    { value: FilterByDate.emissionDate, label: option('emission') },
  ];

  const reportTypeOptions = [
    { value: TypeReport.weekly, label: option('weekly') },
    { value: TypeReport.period, label: option('period') },
    { value: TypeReport.daily, label: option('daily') },
  ];

  return (
    <Form layout="vertical" form={form}>
      <Form.Item
        name="dateFilterBy"
        style={{ margin: '4px 0' }}
        label={
          <Title level={5} style={{ margin: '0' }}>
            {intl({ id: 'cashFlow.label.filterByDate' })}
          </Title>
        }
        initialValue={FilterByDate.dueDate}
      >
        <Radio.Group
          buttonStyle="solid"
          optionType="button"
          size="large"
          options={filterByDateOption}
          style={{ marginBottom: 12 }}
        />
      </Form.Item>

      <FinancialCashFlowDateForm />

      <Form.Item
        name="situation"
        style={{ margin: '4px 0' }}
        label={
          <Title level={5} style={{ margin: '0' }}>
            {intl({ id: 'cashFlow.label.status' })}
          </Title>
        }
        initialValue={[Situation.open, Situation.liquidated]}
      >
        <CheckboxGroupButtons options={situationOption} />
      </Form.Item>

      <Form.Item
        name="typeReport"
        style={{ margin: '4px 0' }}
        label={
          <Title level={5} style={{ margin: '0' }}>
            {intl({ id: 'cashFlow.label.reportType' })}
          </Title>
        }
        initialValue={TypeReport.weekly}
      >
        <Radio.Group
          buttonStyle="solid"
          optionType="button"
          size="large"
          options={reportTypeOptions}
        />
      </Form.Item>

      <Form.Item name="summary" valuePropName="checked" noStyle>
        <Checkbox>{intl({ id: 'cashFlow.checkbox.synthetic' })}</Checkbox>
      </Form.Item>

      <Title level={5} style={{ margin: '4px 0' }}>
        {intl({ id: 'cashFlow.label.show' })}
      </Title>

      <Row gutter={16}>
        <Col>
          <Form.Item
            name="showPayablesAndReceivables"
            valuePropName="checked"
            noStyle
            initialValue={true}
          >
            <Checkbox>
              {intl({ id: 'cashFlow.checkbox.accPayableReceivable' })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="showRate"
            valuePropName="checked"
            noStyle
            initialValue={true}
          >
            <Checkbox>{intl({ id: 'cashFlow.checkbox.tariffs' })}</Checkbox>
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="showBankEntries"
            valuePropName="checked"
            noStyle
            initialValue={true}
          >
            <Checkbox>
              {intl({ id: 'cashFlow.checkbox.singleRelease' })}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <FinancialCashFlowMoreOptions />
    </Form>
  );
}
